// /admin/rule-templates — Rule Templates admin page (T270).
//
// Layout:
//   - Page header + "Create template" button.
//   - Template list: name, kind, severity, language badges, edit/delete actions.
//   - Create/Edit → modal-style inline form above the list.
//   - Each template expands to reveal param definitions and an "Instantiate"
//     form where the user selects a project, fills param values, and
//     creates a project-scoped custom rule.

import { FormEvent, useState } from 'react';
import useSWR from 'swr';
import {
  ChevronDown,
  ChevronRight,
  FileCode2,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Zap,
} from 'lucide-react';
import {
  ADMIN_RULE_TEMPLATES_KEY,
  type ParamDefinition,
  type RuleTemplate,
  type RuleTemplateCreate,
  ruleTemplatesApi,
  type RuleTemplateListResponse,
  fetcher,
} from '../lib/api';
import { FeatureGate } from '../components/FeatureLock';
import { useLicenseState } from '../lib/useLicenseState';
import { PageHeader } from '../components/ui';

// ─── constants ───────────────────────────────────────────────────────────────

const KINDS = ['BUG', 'CODE_SMELL', 'VULNERABILITY', 'SECURITY_HOTSPOT', 'SECRET'];
const SEVERITIES = ['INFO', 'MINOR', 'MAJOR', 'CRITICAL', 'BLOCKER'];
const PATTERN_TYPES = ['contains', 'regexp'];

const severityColor: Record<string, string> = {
  BLOCKER:  'bg-danger/15 text-danger border-danger/30',
  CRITICAL: 'bg-danger/10 text-danger border-danger/20',
  MAJOR:    'bg-warning/15 text-warning border-warning/30',
  MINOR:    'bg-accent/10 text-accent border-accent/20',
  INFO:     'bg-fg-subtle/10 text-fg-muted border-border',
};

// ─── page ────────────────────────────────────────────────────────────────────

export function AdminRuleTemplates() {
  const { data, error, isLoading, mutate } = useSWR<RuleTemplateListResponse>(
    ADMIN_RULE_TEMPLATES_KEY,
    fetcher,
    { revalidateOnFocus: false },
  );

  const [editing, setEditing] = useState<RuleTemplate | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this rule template? Project instances derived from it will no longer be updated.')) return;
    await ruleTemplatesApi.delete(id);
    mutate();
  };

  const handleSaved = () => {
    setShowCreate(false);
    setEditing(null);
    mutate();
  };

  const { loading: licenseLoading, hasFeature } = useLicenseState();
  const canCreate = !licenseLoading && hasFeature('custom_rules');

  return (
    <div className="max-w-4xl mx-auto p-8 w-full">
      <PageHeader
        eyebrow="orbital · admin"
        icon={<FileCode2 />}
        title="Rule Templates"
        description={
          <>
            Parameterized custom-rule recipes. Instantiate a template per project
            to create project-scoped custom rules without writing DSL by hand.
          </>
        }
        actions={
          canCreate && !showCreate && editing === null ? (
            <button onClick={() => setShowCreate(true)} className={primaryBtnCls}>
              <Plus className="h-3.5 w-3.5" aria-hidden="true" />
              Create template
            </button>
          ) : null
        }
      />

      <FeatureGate feature="custom_rules" label="Rule Templates">
        {(showCreate || editing !== null) && (
          <TemplateForm
            initial={editing ?? undefined}
            onSaved={handleSaved}
            onCancel={() => { setShowCreate(false); setEditing(null); }}
          />
        )}

        {(() => {
          if (isLoading) {
            return (
              <div className="orb-card p-8 flex items-center justify-center gap-3 text-fg-muted">
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                <span className="text-sm">Loading rule templates…</span>
              </div>
            );
          } else if (error) {
            return <ErrorBanner />;
          } else if (!data || data.items.length === 0) {
            return (
              <div className="orb-card p-8 text-center text-fg-muted text-sm">
                No rule templates yet. Create one to start defining parameterized custom rules.
              </div>
            );
          } else {
            return (
              <TemplateList
                templates={data.items}
                onEdit={(t) => { setEditing(t); setShowCreate(false); }}
                onDelete={handleDelete}
              />
            );
          }
        })()}
      </FeatureGate>
    </div>
  );
}

// ─── template list ───────────────────────────────────────────────────────────

interface TemplateListProps {
  readonly templates: RuleTemplate[];
  readonly onEdit: (t: RuleTemplate) => void;
  readonly onDelete: (id: string) => void | Promise<void>;
}

function TemplateList({ templates, onEdit, onDelete }: TemplateListProps) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const handleDelete = async (id: string) => {
    setDeleting(id);
    try { await onDelete(id); } finally { setDeleting(null); }
  };

  return (
    <div className="space-y-3">
      {templates.map((t) => (
        <div key={t.id} className="orb-card overflow-hidden">
          {/* Summary row */}
          <div className="flex items-center gap-3 px-4 py-3">
            <button
              onClick={() => setExpanded(expanded === t.id ? null : t.id)}
              className="p-1 text-fg-muted hover:text-fg transition-colors"
              title={expanded === t.id ? 'Collapse' : 'Expand'}
            >
              {expanded === t.id
                ? <ChevronDown className="h-4 w-4" aria-hidden="true" />
                : <ChevronRight className="h-4 w-4" aria-hidden="true" />
              }
            </button>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-fg truncate">{t.name}</p>
              <div className="flex flex-wrap items-center gap-1.5 mt-1">
                <Chip>{t.kind}</Chip>
                <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded border font-semibold ${severityColor[t.severity] ?? 'bg-bg-inset text-fg-muted border-border'}`}>
                  {t.severity}
                </span>
                <Chip>{t.language}</Chip>
                <Chip>{t.pattern_type}</Chip>
                {(t.param_definitions?.length ?? 0) > 0 && (
                  <Chip>{t.param_definitions.length} param{t.param_definitions.length === 1 ? '' : 's'}</Chip>
                )}
              </div>
            </div>

            <button
              onClick={() => onEdit(t)}
              className="p-1.5 rounded text-fg-muted hover:text-accent hover:bg-accent/10 transition-colors"
              title="Edit template"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
            </button>

            <button
              onClick={() => handleDelete(t.id)}
              disabled={deleting === t.id}
              className="p-1.5 rounded text-fg-muted hover:text-danger hover:bg-danger/10 transition-colors disabled:opacity-30"
              title="Delete template"
            >
              {deleting === t.id
                ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                : <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              }
            </button>
          </div>

          {/* Detail panel */}
          {expanded === t.id && (
            <TemplateDetail template={t} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── template detail + instantiate ───────────────────────────────────────────

function TemplateDetail({ template }: { readonly template: RuleTemplate }) {
  const [showInstantiate, setShowInstantiate] = useState(false);

  return (
    <div className="border-t border-border">
      {/* Description */}
      <div className="px-4 py-3 space-y-2 text-sm">
        {template.description && (
          <p className="text-fg-muted">{template.description}</p>
        )}
        <div className="font-mono text-xs bg-bg-inset border border-border rounded p-2 whitespace-pre-wrap break-all text-fg-muted">
          {template.pattern_template}
        </div>
        {(template.param_definitions?.length ?? 0) > 0 && (
          <div>
            <p className="orb-section-title text-[10px] mb-1">Parameters</p>
            <ul className="space-y-1">
              {template.param_definitions?.map((p) => (
                <li key={p.name} className="flex items-start gap-2 text-xs">
                  <code className="font-mono text-accent bg-accent/10 px-1 py-0.5 rounded shrink-0">
                    {p.name}
                    {p.required ? ' *' : ''}
                  </code>
                  <span className="text-fg-muted">
                    {p.description}
                    {p.default ? ` (default: ${p.default})` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {(template.tags?.length > 0) && (
          <div className="flex flex-wrap gap-1">
            {template.tags.map((tag) => (
              <span key={tag} className="text-[10px] bg-bg-inset border border-border rounded px-1.5 py-0.5 text-fg-muted">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Instantiate */}
      <div className="border-t border-border px-4 py-3">
        {showInstantiate ? (
          <InstantiateForm
            template={template}
            onDone={() => setShowInstantiate(false)}
          />
        ) : (
          <button
            onClick={() => setShowInstantiate(true)}
            className="inline-flex items-center gap-1.5 text-xs text-accent hover:text-accent/80 font-medium transition-colors"
          >
            <Zap className="h-3.5 w-3.5" aria-hidden="true" />
            Instantiate for a project
          </button>
        )}
      </div>
    </div>
  );
}

// ─── instantiate form ─────────────────────────────────────────────────────────

interface InstantiateFormProps {
  readonly template: RuleTemplate;
  readonly onDone: () => void;
}

function InstantiateForm({ template, onDone }: InstantiateFormProps) {
  const [projectID, setProjectID] = useState('');
  const [ruleIDSuffix, setRuleIDSuffix] = useState('');
  const [params, setParams] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const p of template.param_definitions) {
      init[p.name] = p.default ?? '';
    }
    return init;
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setErr(null);
    setSuccess(null);
    if (!projectID.trim()) { setErr('Project ID is required.'); return; }
    if (!ruleIDSuffix.trim()) { setErr('Rule ID suffix is required.'); return; }
    for (const p of template.param_definitions) {
      if (p.required && !params[p.name]?.trim()) {
        setErr(`Parameter "${p.name}" is required.`);
        return;
      }
    }
    setSaving(true);
    try {
      const inst = await ruleTemplatesApi.instantiate(projectID.trim(), template.id, {
        rule_id_suffix: ruleIDSuffix.trim(),
        params,
      });
      setSuccess(`Created rule "custom:${inst.rule_id.replace('custom:', '')}" on project ${projectID.slice(0, 8)}….`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Instantiation failed.');
    } finally {
      setSaving(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-2">
        <p className="text-xs text-success">{success}</p>
        <div className="flex gap-3">
          <button onClick={() => { setSuccess(null); setProjectID(''); setRuleIDSuffix(''); }} className="text-xs text-accent hover:underline">
            Instantiate again
          </button>
          <button onClick={onDone} className="text-xs text-fg-muted hover:text-fg transition-colors">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <p className="text-xs font-semibold text-fg">Instantiate on a project</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="inst-project" className="orb-section-title text-[10px]">Project ID (UUID)</label>
          <input
            id="inst-project"
            type="text"
            value={projectID}
            onChange={(e) => setProjectID(e.target.value)}
            className={inputCls}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            autoFocus
          />
        </div>
        <div className="flex flex-col gap-1">
          <label htmlFor="inst-suffix" className="orb-section-title text-[10px]">
            Rule ID suffix <span className="text-fg-subtle font-mono">(custom:&lt;suffix&gt;)</span>
          </label>
          <input
            id="inst-suffix"
            type="text"
            value={ruleIDSuffix}
            onChange={(e) => setRuleIDSuffix(e.target.value)}
            className={inputCls}
            placeholder="my-rule-name"
          />
        </div>
      </div>

      {(template.param_definitions ?? []).map((p) => (
        <div key={p.name} className="flex flex-col gap-1">
          <label className="orb-section-title text-[10px]">
            {p.name}{p.required ? ' *' : ''}
            {p.description && <span className="font-normal text-fg-subtle ml-1">{p.description}</span>}
          </label>
          <input
            type="text"
            value={params[p.name] ?? ''}
            onChange={(e) => setParams({ ...params, [p.name]: e.target.value })}
            className={inputCls}
            placeholder={p.default ?? ''}
          />
        </div>
      ))}

      {err && <p role="alert" className="text-xs text-danger">{err}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {saving
            ? <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Creating…</>
            : <><Zap className="h-3.5 w-3.5" aria-hidden="true" /> Instantiate</>
          }
        </button>
        <button type="button" onClick={onDone} className="text-xs text-fg-muted hover:text-fg transition-colors px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── create / edit form ───────────────────────────────────────────────────────

interface TemplateFormProps {
  readonly initial?: RuleTemplate;
  readonly onSaved: () => void;
  readonly onCancel: () => void;
}

function TemplateForm({ initial, onSaved, onCancel }: TemplateFormProps) {
  const isEdit = Boolean(initial);

  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [whyItMatters, setWhyItMatters] = useState(initial?.why_it_matters ?? '');
  const [howToFix, setHowToFix] = useState(initial?.how_to_fix ?? '');
  const [kind, setKind] = useState(initial?.kind ?? 'CODE_SMELL');
  const [severity, setSeverity] = useState(initial?.severity ?? 'MAJOR');
  const [language, setLanguage] = useState(initial?.language ?? '*');
  const [patternType, setPatternType] = useState(initial?.pattern_type ?? 'contains');
  const [patternTemplate, setPatternTemplate] = useState(initial?.pattern_template ?? '');
  const [paramDefs, setParamDefs] = useState<ParamDefinition[]>(
    initial?.param_definitions ?? [],
  );
  const [tags, setTags] = useState((initial?.tags ?? []).join(', '));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const handleSubmit = async (ev: FormEvent) => {
    ev.preventDefault();
    setErr(null);
    const body: RuleTemplateCreate = {
      name: name.trim(),
      description: description.trim(),
      why_it_matters: whyItMatters.trim(),
      how_to_fix: howToFix.trim(),
      kind,
      severity,
      language: language.trim() || '*',
      pattern_type: patternType,
      pattern_template: patternTemplate,
      param_definitions: paramDefs,
      tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
    };
    setSaving(true);
    try {
      if (isEdit && initial) {
        await ruleTemplatesApi.update(initial.id, body);
      } else {
        await ruleTemplatesApi.create(body);
      }
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const addParam = () =>
    setParamDefs([...paramDefs, { name: '', description: '', required: false, default: '' }]);

  const removeParam = (idx: number) =>
    setParamDefs(paramDefs.filter((_, i) => i !== idx));

  const updateParam = (idx: number, field: keyof ParamDefinition, value: string | boolean) =>
    setParamDefs(paramDefs.map((p, i) => i === idx ? { ...p, [field]: value } : p));

  return (
    <form onSubmit={handleSubmit} className="orb-card mb-6 p-5 space-y-5">
      <p className="text-sm font-semibold text-fg">
        {isEdit ? `Edit — ${initial!.name}` : 'New rule template'}
      </p>

      {/* Basic fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-name" className={labelCls}>Name *</label>
          <input id="rt-name" type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="No-TODO comments" autoFocus />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-lang" className={labelCls}>Language <span className="font-normal text-fg-subtle">(* for all)</span></label>
          <input id="rt-lang" type="text" value={language} onChange={(e) => setLanguage(e.target.value)} className={inputCls} placeholder="* or py, go, java…" />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <label htmlFor="rt-desc" className={labelCls}>Description *</label>
        <input id="rt-desc" type="text" value={description} onChange={(e) => setDescription(e.target.value)} className={inputCls} placeholder="Short, one-line description shown in the rule catalog." />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-kind" className={labelCls}>Kind *</label>
          <select id="rt-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={inputCls}>
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-sev" className={labelCls}>Severity *</label>
          <select id="rt-sev" value={severity} onChange={(e) => setSeverity(e.target.value)} className={inputCls}>
            {SEVERITIES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-ptype" className={labelCls}>Pattern type *</label>
          <select id="rt-ptype" value={patternType} onChange={(e) => setPatternType(e.target.value)} className={inputCls}>
            {PATTERN_TYPES.map((pt) => <option key={pt} value={pt}>{pt}</option>)}
          </select>
        </div>
      </div>

      {/* Pattern template */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="rt-pattern" className={labelCls}>
          Pattern template * <span className="font-normal text-fg-subtle">— use {'{{PARAM_NAME}}'} as placeholders</span>
        </label>
        <textarea
          id="rt-pattern"
          value={patternTemplate}
          onChange={(e) => setPatternTemplate(e.target.value)}
          className={`${inputCls} font-mono min-h-[72px] resize-y`}
          placeholder="TODO|FIXME|{{KEYWORD}}"
        />
      </div>

      {/* Optional docs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-why" className={labelCls}>Why it matters</label>
          <textarea id="rt-why" value={whyItMatters} onChange={(e) => setWhyItMatters(e.target.value)} className={`${inputCls} min-h-[56px] resize-y`} placeholder="Explain the risk or smell." />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="rt-fix" className={labelCls}>How to fix</label>
          <textarea id="rt-fix" value={howToFix} onChange={(e) => setHowToFix(e.target.value)} className={`${inputCls} min-h-[56px] resize-y`} placeholder="Suggest a concrete remediation." />
        </div>
      </div>

      {/* Tags */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="rt-tags" className={labelCls}>Tags <span className="font-normal text-fg-subtle">(comma-separated)</span></label>
        <input id="rt-tags" type="text" value={tags} onChange={(e) => setTags(e.target.value)} className={inputCls} placeholder="security, style, owasp-a3" />
      </div>

      {/* Param definitions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <p className={labelCls}>Parameter definitions</p>
          <button type="button" onClick={addParam} className="inline-flex items-center gap-1 text-[11px] text-accent hover:text-accent/80 font-medium transition-colors">
            <Plus className="h-3 w-3" aria-hidden="true" /> Add param
          </button>
        </div>
        {paramDefs.length === 0 ? (
          <p className="text-xs text-fg-subtle italic">No parameters — the pattern is fixed. Add params to make it configurable per project.</p>
        ) : (
          <div className="space-y-2">
            {paramDefs.map((p, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={idx} className="grid grid-cols-[1fr_2fr_auto_auto] gap-2 items-center">
                <input
                  type="text"
                  value={p.name}
                  onChange={(e) => updateParam(idx, 'name', e.target.value)}
                  className={inputCls}
                  placeholder="PARAM_NAME"
                />
                <input
                  type="text"
                  value={p.description}
                  onChange={(e) => updateParam(idx, 'description', e.target.value)}
                  className={inputCls}
                  placeholder="Description"
                />
                <label className="flex items-center gap-1 text-[11px] text-fg-muted whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={p.required}
                    onChange={(e) => updateParam(idx, 'required', e.target.checked)}
                    className="accent-accent h-3.5 w-3.5"
                  />
                  {'required'}
                </label>
                <button
                  type="button"
                  onClick={() => removeParam(idx)}
                  className="p-1 text-fg-muted hover:text-danger transition-colors"
                  title="Remove parameter"
                >
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {err && <p role="alert" className="text-xs text-danger">{err}</p>}

      <div className="flex items-center gap-3">
        <button type="submit" disabled={saving} className={primaryBtnCls}>
          {(() => {
            if (saving) {
              return <><Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> Saving…</>;
            } else if (isEdit) {
              return 'Save changes';
            } else {
              return 'Create template';
            }
          })()}
        </button>
        <button type="button" onClick={onCancel} className="text-xs text-fg-muted hover:text-fg transition-colors px-2">
          Cancel
        </button>
      </div>
    </form>
  );
}

// ─── error state ──────────────────────────────────────────────────────────────

function ErrorBanner() {
  return (
    <div role="alert" className="orb-card p-6 border-danger/40 bg-danger/5">
      <p className="orb-eyebrow text-danger mb-2">request failed</p>
      <p className="text-sm text-fg">Could not load rule templates.</p>
      <p className="text-xs text-fg-muted mt-2">
        Only org admins can manage rule templates. Verify your role or check
        the API server logs.
      </p>
    </div>
  );
}

// ─── small reusable chip ──────────────────────────────────────────────────────

function Chip({ children }: { readonly children: React.ReactNode }) {
  return (
    <span className="inline-block text-[10px] bg-bg-inset border border-border rounded px-1.5 py-0.5 text-fg-muted font-mono">
      {children}
    </span>
  );
}

// ─── shared styles ────────────────────────────────────────────────────────────

const labelCls = 'orb-section-title text-[11px]';

const inputCls = `
  w-full px-3 py-2 rounded-md border border-border
  bg-bg-inset text-fg text-sm placeholder-fg-subtle
  focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/50
  transition-colors
`.trim();

const primaryBtnCls = `
  inline-flex items-center gap-1.5 px-4 py-2 rounded-md
  text-xs font-semibold uppercase tracking-[0.08em]
  bg-accent text-accent-fg hover:opacity-90
  border border-accent/40 transition-opacity
  disabled:opacity-30 disabled:cursor-not-allowed
`.trim();
